import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../supabase';

const { FiAward, FiPenTool, FiCheck, FiLoader, FiTarget } = FiIcons;

const Team = () => {
  const [teamImageUrl, setTeamImageUrl] = useState("https://www.dropbox.com/scl/fi/tgctp5nq9wcmsd1tpoe1k/IMG_2472.jpg?rlkey=1djol27usjf38hkvj1hgg4pgu&st=8g8xqrem&dl=1");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeamImage = async () => {
      try {
        const { data, error } = await supabase
          .from('settings_1740580000000')
          .select('value')
          .eq('key', 'team_image')
          .single();
        
        if (data?.value) setTeamImageUrl(data.value);
      } catch (err) {
        console.error("Error fetching team image:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTeamImage();
  }, []);

  const founders = [
    {
      name: "Iga",
      role: "Certified PQ Coach",
      icon: FiAward,
      bio: "A certified Positive Intelligence (PQ) Coach, Iga specializes in mental fitness. She helps you identify and intercept 'saboteurs' that block creativity, guiding you toward a 'Sage' perspective where clarity and calm reside.",
      specialty: "Mental Fitness & Growth Mindset"
    },
    {
      name: "Iman",
      role: "Professional Artist",
      icon: FiPenTool,
      bio: "An Evanston-based artist with a passion for intuitive expression. Iman brings the technical soul to every session, teaching you that art isn't about perfection—it's about the courage to put color to canvas and let your soul speak.",
      specialty: "Intuitive Art & Color Theory"
    }
  ];

  return (
    <section id="team" className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="absolute -top-10 -left-10 w-64 h-64 bg-rose-100 rounded-full blur-3xl opacity-60 animate-pulse"></div>
            <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-purple-100 rounded-full blur-3xl opacity-60 animate-pulse" style={{ animationDelay: '1s' }}></div>
            
            <div className="relative z-10 p-4 bg-white rounded-[3rem] shadow-2xl border border-gray-100 transform -rotate-2 hover:rotate-0 transition-transform duration-500">
              <div className="rounded-[2.5rem] overflow-hidden aspect-[4/5] bg-gray-100 flex items-center justify-center">
                {loading ? (
                  <SafeIcon icon={FiLoader} className="w-12 h-12 text-rose-300 animate-spin" />
                ) : (
                  <img 
                    src={teamImageUrl} 
                    alt="Iga and Iman - Founders" 
                    className="w-full h-full object-cover scale-105 hover:scale-100 transition-transform duration-700"
                  />
                )}
              </div>
              
              <motion.div 
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-6 -right-6 bg-gradient-to-r from-rose-500 to-purple-600 text-white p-6 rounded-3xl shadow-xl flex items-center space-x-3"
              >
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-md">
                  <SafeIcon icon={FiTarget} className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Our Philosophy</div>
                  <div className="font-bold text-sm sm:text-base">Coaching meets Creativity</div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          <div className="space-y-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="bg-gradient-to-r from-rose-600 to-purple-600 bg-clip-text text-transparent">Meet the Founders</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Canvas & Clarity was born from a friendship between a coach and an artist who realized that the most beautiful masterpieces are the ones we create within ourselves.
              </p>
            </motion.div>

            <div className="space-y-8">
              {founders.map((founder, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="bg-gray-50 p-8 rounded-[2rem] border border-gray-100 hover:bg-white hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-rose-100 p-3 rounded-xl flex-shrink-0">
                      <SafeIcon icon={founder.icon} className="w-6 h-6 text-rose-600" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800">{founder.name}</h3>
                      <p className="text-rose-500 font-bold text-sm uppercase tracking-widest mb-3">{founder.role}</p>
                      <p className="text-gray-600 leading-relaxed mb-4 text-sm sm:text-base">{founder.bio}</p>
                      <div className="flex items-center text-xs sm:text-sm font-semibold text-gray-400">
                        <SafeIcon icon={FiCheck} className="text-green-500 mr-2" />
                        Specialty: {founder.specialty}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Team;